'use client';

import { useEffect } from 'react';

import { validateBrandingUrl } from '~/lib/utils/validate-branding-url';

interface DynamicFaviconProps {
  faviconUrl: string | null;
}

/**
 * Get MIME type from favicon URL extension
 * Handles URLs with query parameters (e.g., ?v=timestamp for cache-busting)
 */
function getMimeType(url: string): string {
  // Remove query parameters before extracting extension
  const urlWithoutQuery = url.split('?')[0];
  const extension = urlWithoutQuery?.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'ico':
      return 'image/x-icon';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'svg':
      return 'image/svg+xml';
    default:
      return 'image/x-icon';
  }
}

/**
 * Updates the favicon to match tenant branding.
 *
 * Instead of removing and recreating link elements (which breaks React's
 * fiber tree for Next.js metadata-managed nodes and causes removeChild crashes
 * during navigation), this updates existing elements' href in place and only
 * creates new elements if needed.
 */
export function DynamicFavicon({ faviconUrl }: DynamicFaviconProps) {
  useEffect(() => {
    // SECURITY: Validate URL to prevent SSRF and XSS attacks
    const validatedUrl = validateBrandingUrl(faviconUrl);
    if (!validatedUrl) {
      return;
    }

    const mimeType = getMimeType(validatedUrl);

    // Update existing icon link in place (may be managed by Next.js metadata API)
    const existingIcon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (existingIcon) {
      existingIcon.href = validatedUrl;
      existingIcon.type = mimeType;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = mimeType;
      link.href = validatedUrl;
      document.head.appendChild(link);
    }

    // Update or create shortcut icon for older browsers
    const existingShortcut = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement | null;
    if (existingShortcut) {
      existingShortcut.href = validatedUrl;
      existingShortcut.type = mimeType;
    } else {
      const shortcutLink = document.createElement('link');
      shortcutLink.rel = 'shortcut icon';
      shortcutLink.type = mimeType;
      shortcutLink.href = validatedUrl;
      document.head.appendChild(shortcutLink);
    }

    // Update or create apple-touch-icon
    const urlWithoutQuery = validatedUrl.split('?')[0];
    const isImageFormat = urlWithoutQuery?.endsWith('.png') || urlWithoutQuery?.endsWith('.jpg') || urlWithoutQuery?.endsWith('.jpeg');

    if (isImageFormat) {
      const existingApple = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
      if (existingApple) {
        existingApple.href = validatedUrl;
      } else {
        const appleLink = document.createElement('link');
        appleLink.rel = 'apple-touch-icon';
        appleLink.href = validatedUrl;
        document.head.appendChild(appleLink);
      }
    }
  }, [faviconUrl]);

  return null;
}
